import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { waitlist } from "../content/site";
import { submitWaitlist } from "../lib/submitHandlers";
import { Button, Container, Field, PillSelect, Reveal, TextInput } from "./ui";

const EMPTY = { name: "", mobile: "", email: "", area: "", vehicleType: "" };

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Please enter your name.";
  if (!/^[6-9]\d{9}$/.test(form.mobile.trim()))
    errors.mobile = "Enter a valid 10-digit mobile number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
    errors.email = "Enter a valid email address.";
  if (!form.area.trim()) errors.area = "Please enter your area.";
  if (!form.vehicleType) errors.vehicleType = "Please select a vehicle type.";
  return errors;
}

export default function Waitlist() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus("submitting");
    const result = await submitWaitlist(form);
    if (result.ok) {
      setStatus("success");
      setForm(EMPTY);
    } else {
      setStatus("idle");
    }
  };

  return (
    <section id="waitlist" className="bg-white py-20 sm:py-28">
      <Container>
        <Reveal className="mx-auto max-w-xl">
          <div className="rounded-[32px] border border-navy/8 bg-[var(--color-bg-soft)] p-6 shadow-navy sm:p-10">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center py-10 text-center"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10 text-success">
                    <CheckCircle2 size={34} />
                  </span>
                  <h3 className="mt-6 text-2xl font-extrabold text-navy">
                    {waitlist.successTitle}
                  </h3>
                  <p className="mt-2 text-base font-semibold text-navy/50">
                    {waitlist.successSub}
                  </p>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h2 className="text-center text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                    {waitlist.headline}
                  </h2>

                  <form onSubmit={handleSubmit} noValidate className="mt-8 flex flex-col gap-5">
                    <Field label="Name" id="wl-name" error={errors.name}>
                      <TextInput
                        id="wl-name"
                        name="name"
                        autoComplete="name"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={setField("name")}
                        error={errors.name}
                      />
                    </Field>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Mobile Number" id="wl-mobile" error={errors.mobile}>
                        <TextInput
                          id="wl-mobile"
                          name="mobile"
                          inputMode="numeric"
                          autoComplete="tel"
                          placeholder="98765 43210"
                          value={form.mobile}
                          onChange={setField("mobile")}
                          error={errors.mobile}
                        />
                      </Field>
                      <Field label="Email" id="wl-email" error={errors.email}>
                        <TextInput
                          id="wl-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={setField("email")}
                          error={errors.email}
                        />
                      </Field>
                    </div>

                    <Field label="Area" id="wl-area" error={errors.area}>
                      <TextInput
                        id="wl-area"
                        name="area"
                        placeholder="e.g. Singanallur"
                        value={form.area}
                        onChange={setField("area")}
                        error={errors.area}
                      />
                    </Field>

                    <Field label="Vehicle Type" error={errors.vehicleType}>
                      <PillSelect
                        name="vehicleType"
                        options={waitlist.vehicleTypes}
                        value={form.vehicleType}
                        onChange={setField("vehicleType")}
                      />
                    </Field>

                    <Button
                      type="submit"
                      size="lg"
                      className="mt-2 w-full"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={18} className="animate-spin" /> Submitting…
                        </>
                      ) : (
                        waitlist.cta
                      )}
                    </Button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
